/**
 * Analytics tracking utility for Honey app
 * Tracks user behavior across different feature categories
 */

const API_URL = window.location.origin;

// Device type detection
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width >= 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
};

// Event categories
export const EventCategory = {
  RECIPE_MANAGEMENT: 'recipe_management',
  COOKING_WORKFLOW: 'cooking_workflow',
  AI_FEATURES: 'ai_features',
  SOCIAL: 'social'
};

// Event names
export const EventName = {
  // Recipe Management
  RECIPE_CREATED_CHAT: 'recipe_created_chat',
  RECIPE_CREATED_URL: 'recipe_created_url',
  RECIPE_VIEWED: 'recipe_viewed',
  RECIPE_EDITED: 'recipe_edited',
  RECIPE_DELETED: 'recipe_deleted',
  RECIPE_PRINTED: 'recipe_printed',
  DIET_PREFERENCES_SET: 'diet_preferences_set',
  
  // Cooking Workflow
  CURRENTLY_COOKING_ADDED: 'currently_cooking_added',
  CURRENTLY_COOKING_REMOVED: 'currently_cooking_removed',
  SHOPPING_LIST_OPENED: 'shopping_list_opened',
  SHOPPING_LIST_PRINTED: 'shopping_list_printed',
  MISE_EN_PLACE_GENERATED: 'mise_en_place_generated',
  RECIPE_MARKED_COOKED: 'recipe_marked_cooked',
  
  // Groceries
  GROCERY_LIST_OPENED: 'grocery_list_opened',
  GROCERY_ITEM_ADDED: 'grocery_item_added',
  GROCERY_ITEM_CHECKED: 'grocery_item_checked',
  GROCERY_LIST_LINKED_TO_HIVE: 'grocery_list_linked_to_hive',
  GROCERY_LIST_PRINTED: 'grocery_list_printed',
  
  // AI Features
  CHAT_SESSION_STARTED: 'chat_session_started',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  AI_RECIPE_GENERATED: 'ai_recipe_generated',
  
  // Social
  HIVE_CREATED: 'hive_created',
  HIVE_JOINED: 'hive_joined',
  HIVE_LEFT: 'hive_left',
  BUZZ_POSTED: 'buzz_posted',
  BUZZ_LIKED: 'buzz_liked',
  BUZZ_COMMENTED: 'buzz_commented',
  RECIPE_SAVED_FROM_FEED: 'recipe_saved_from_feed',
  FEED_VIEWED: 'feed_viewed'
};

/**
 * Track an analytics event
 * @param {string} eventName - The event name from EventName
 * @param {string} eventCategory - The category from EventCategory
 * @param {object} eventData - Additional contextual data
 */
export const trackEvent = async (eventName, eventCategory, eventData = {}) => {
  try {
    const deviceType = getDeviceType();
    
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        event_name: eventName,
        event_category: eventCategory,
        event_data: eventData,
        device_type: deviceType
      })
    });
  } catch (error) {
    // Silently fail - analytics shouldn't break the app
    console.debug('Analytics tracking failed:', error);
  }
};

// Convenience functions for common events

// Recipe Management
export const trackRecipeCreatedChat = (recipeId, title) => {
  trackEvent(EventName.RECIPE_CREATED_CHAT, EventCategory.RECIPE_MANAGEMENT, { recipe_id: recipeId, title });
};

export const trackRecipeCreatedUrl = (recipeId, title, sourceUrl) => {
  trackEvent(EventName.RECIPE_CREATED_URL, EventCategory.RECIPE_MANAGEMENT, { recipe_id: recipeId, title, source_url: sourceUrl });
};

export const trackRecipeViewed = (recipeId) => {
  trackEvent(EventName.RECIPE_VIEWED, EventCategory.RECIPE_MANAGEMENT, { recipe_id: recipeId });
};

export const trackRecipePrinted = (recipeId) => {
  trackEvent(EventName.RECIPE_PRINTED, EventCategory.RECIPE_MANAGEMENT, { recipe_id: recipeId });
};

export const trackDietPreferencesSet = (preferences) => {
  trackEvent(EventName.DIET_PREFERENCES_SET, EventCategory.RECIPE_MANAGEMENT, { preferences });
};

// Cooking Workflow
export const trackCurrentlyCookingAdded = (recipeId) => {
  trackEvent(EventName.CURRENTLY_COOKING_ADDED, EventCategory.COOKING_WORKFLOW, { recipe_id: recipeId });
};

export const trackCurrentlyCookingRemoved = (recipeId) => {
  trackEvent(EventName.CURRENTLY_COOKING_REMOVED, EventCategory.COOKING_WORKFLOW, { recipe_id: recipeId });
};

export const trackShoppingListOpened = (recipeCount) => {
  trackEvent(EventName.SHOPPING_LIST_OPENED, EventCategory.COOKING_WORKFLOW, { recipe_count: recipeCount });
};

export const trackShoppingListPrinted = (recipeCount) => {
  trackEvent(EventName.SHOPPING_LIST_PRINTED, EventCategory.COOKING_WORKFLOW, { recipe_count: recipeCount });
};

export const trackMiseEnPlaceGenerated = (recipeCount) => {
  trackEvent(EventName.MISE_EN_PLACE_GENERATED, EventCategory.COOKING_WORKFLOW, { recipe_count: recipeCount });
};

export const trackRecipeMarkedCooked = (recipeId) => {
  trackEvent(EventName.RECIPE_MARKED_COOKED, EventCategory.COOKING_WORKFLOW, { recipe_id: recipeId });
};

// Groceries
export const trackGroceryListOpened = (itemCount, isHiveLinked = false) => {
  trackEvent(EventName.GROCERY_LIST_OPENED, EventCategory.COOKING_WORKFLOW, { item_count: itemCount, is_hive_linked: isHiveLinked });
};

export const trackGroceryItemAdded = (itemName, isHiveLinked = false) => {
  trackEvent(EventName.GROCERY_ITEM_ADDED, EventCategory.COOKING_WORKFLOW, { item_name: itemName, is_hive_linked: isHiveLinked });
};

export const trackGroceryItemChecked = (itemName, isHiveLinked = false) => {
  trackEvent(EventName.GROCERY_ITEM_CHECKED, EventCategory.COOKING_WORKFLOW, { item_name: itemName, is_hive_linked: isHiveLinked });
};

export const trackGroceryListLinkedToHive = (hiveId) => {
  trackEvent(EventName.GROCERY_LIST_LINKED_TO_HIVE, EventCategory.COOKING_WORKFLOW, { hive_id: hiveId });
};

export const trackGroceryListPrinted = (itemCount, isHiveLinked = false) => {
  trackEvent(EventName.GROCERY_LIST_PRINTED, EventCategory.COOKING_WORKFLOW, { item_count: itemCount, is_hive_linked: isHiveLinked });
};

// AI Features
export const trackChatSessionStarted = (chatId) => {
  trackEvent(EventName.CHAT_SESSION_STARTED, EventCategory.AI_FEATURES, { chat_id: chatId });
};

export const trackChatMessageSent = (chatId, messageLength) => {
  trackEvent(EventName.CHAT_MESSAGE_SENT, EventCategory.AI_FEATURES, { chat_id: chatId, message_length: messageLength });
};

export const trackAiRecipeGenerated = (recipeId, chatId) => {
  trackEvent(EventName.AI_RECIPE_GENERATED, EventCategory.AI_FEATURES, { recipe_id: recipeId, chat_id: chatId });
};

// Social
export const trackHiveCreated = (hiveId, hiveName, isPrivate) => {
  trackEvent(EventName.HIVE_CREATED, EventCategory.SOCIAL, { hive_id: hiveId, hive_name: hiveName, is_private: isPrivate });
};

export const trackHiveJoined = (hiveId) => {
  trackEvent(EventName.HIVE_JOINED, EventCategory.SOCIAL, { hive_id: hiveId });
};

export const trackBuzzPosted = (buzzId, hiveIds, hasMedia, hasRating) => {
  trackEvent(EventName.BUZZ_POSTED, EventCategory.SOCIAL, { buzz_id: buzzId, hive_count: hiveIds?.length || 0, has_media: hasMedia, has_rating: hasRating });
};

export const trackBuzzLiked = (buzzId) => {
  trackEvent(EventName.BUZZ_LIKED, EventCategory.SOCIAL, { buzz_id: buzzId });
};

export const trackBuzzCommented = (buzzId) => {
  trackEvent(EventName.BUZZ_COMMENTED, EventCategory.SOCIAL, { buzz_id: buzzId });
};

export const trackRecipeSavedFromFeed = (recipeId, sourceUserId) => {
  trackEvent(EventName.RECIPE_SAVED_FROM_FEED, EventCategory.SOCIAL, { recipe_id: recipeId, source_user_id: sourceUserId });
};

export const trackFeedViewed = (tab) => {
  trackEvent(EventName.FEED_VIEWED, EventCategory.SOCIAL, { tab });
};

export default {
  trackEvent,
  EventCategory,
  EventName,
  // Recipe Management
  trackRecipeCreatedChat,
  trackRecipeCreatedUrl,
  trackRecipeViewed,
  trackRecipePrinted,
  trackDietPreferencesSet,
  // Cooking Workflow
  trackCurrentlyCookingAdded,
  trackCurrentlyCookingRemoved,
  trackShoppingListOpened,
  trackShoppingListPrinted,
  trackMiseEnPlaceGenerated,
  trackRecipeMarkedCooked,
  // Groceries
  trackGroceryListOpened,
  trackGroceryItemAdded,
  trackGroceryItemChecked,
  trackGroceryListLinkedToHive,
  trackGroceryListPrinted,
  // AI Features
  trackChatSessionStarted,
  trackChatMessageSent,
  trackAiRecipeGenerated,
  // Social
  trackHiveCreated,
  trackHiveJoined,
  trackBuzzPosted,
  trackBuzzLiked,
  trackBuzzCommented,
  trackRecipeSavedFromFeed,
  trackFeedViewed
};
